<template lang="pug">
    .play-box(
        :class="{\
            'play-box--warning': isNeedRotate,\
            'play-box_limited': parameters && parameters.difficult.amountOfRows <= 6\
        }"
    )
        .warning(v-if="isNeedRotate")
            template
                svg#rotate(
                    version='1.1',
                    xmlns='http://www.w3.org/2000/svg',
                    x='0px', y='0px', width='40px', height='40px',
                    viewBox='0 0 612 612',
                    style='enable-background:new 0 0 612 612;',
                    xml:space='preserve'
                )
                    rotation-icon
                div
                    | {{ $i18n.t('map.map_mobile_rotateText') }}
        template(v-if='!isLoading && !isNeedRotate')
            template
                .play-box__game
                    game-header(
                        v-show='!gameFinished && gameStarted'
                        @soundStateChanged="headerKey = !headerKey",
                        :key="headerKey",
                        :time='getTime'
                        :slug="type"
                        :gameType="parameters.courseType"
                        :is-homework="!!exerciseId"
                    )
                    .play-box__example
                        associations-table.play-box__table(
                            v-if='!gameFinished',
                            :is-flipping="isFlipping",
                            :classifications-table='classificationsTable',
                            :game-started='gameStarted',
                            :links-type='typeOfLinksInChain',
                            :drag-started='dragStarted',
                            :helper-mode-on='helperModeOn',
                            :table-size='parameters.difficult',
                            :link-dropped='linkDropped',
                            @processReply='processReply',
                            @displayPlayButton='showPlayButton = true',
                            @dropItemInMatrix='dropItemInMatrix',
                            @linkResized='linkDropped = false'
                            @dragStarted="isDragging = true"
                            @dragEnded="isDragging = false"
                        )
                        .play-box__buttons.play-box__buttons--game(v-if="!gameFinished")
                            cs-button.play-box__button#reply-button(
                                v-if='gameStarted && !helperModeOn',
                                :disabled='disableReplyButton',
                                @click='processReply'
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_reply') }}
                            cs-button.play-box__button(
                                v-if="gameStarted && typeOfLinksInChain !== 'image' && !helperModeOn && !isOlympiad",
                                @click='showCorrectAnswers',
                                :disabled='!disableReplyButton'
                            )
                                | {{ $i18n.t('trainer.trainer_gamePlayBox_forgot') }}
                            cs-button.play-box__button(
                                v-if='helperModeOn',
                                @click='processReply'
                            )
                                | {{ memorizedButtonName }}
                    drop.play-box__drop(@drop='dropItemInAnswersBox', v-if='!gameFinished && showAnswersContainer')
                            answer-container(
                                v-show="displayAnswersContainer"
                                :answers='itemsForAnswersBox',
                                :game-started='gameStarted',
                                :links-type='typeOfLinksInChain',
                                @dragStarted='dragStarted = true',
                                @dragEnded='dragStarted = false'
                            )
                .play-box__end-container(v-if='gameFinished && !isOlympiad')
                    fireworks(
                        v-if="gameFinished && !isOlympiad"
                        :is-mobile="isMobile"
                    )
                    .play-box__reaction-container
                        result-image.play-box__image(status="success")
                    result-buttons(
                        :exercise-id="exerciseId"
                        :games-type="parameters.courseType"
                        show-restart-button
                        @restartGame="restartGame"
                    )
                        slot
        cs-loader(v-if='isLoading && !isNeedRotate')
</template>

<script>
    import AssociationsGameApi from '../../../../api/games/associationsGameApi.js';
    import { Drop } from 'vue-drag-drop';
    import _ from 'lodash';
    import playBoxGameMixin from '../../../../utils/trainer/playBoxGameMixin.js';
    import olympiadAnswerTimerMixin from '../../../../utils/olympiads/olympiadAnswerTimerMixin.js';
    import AssociationsTable from './AssociationsTable.vue';
    import AnswerContainer from './AnswerContainer.vue';

    const CORRECT_LINK = 'correct-link';
    const DEFAULT_VOLUME = 0.6;

    function DEFAULT_DATA() {
        return {
            classificationsTable: [],
            itemsForAnswersBox: [],
            correctMatrix: [],
            typeOfLinksInChain: '',
            memorized: true,
            showPlayButton: false,
            chainOfAttempts: [],
            dragStarted: false,
            helperModeOn: false,
            prepareForGame: true,
            amountOfExamples: 0,
            hideGameHeader: false,
            isAttemptCorrect: false,
            linkDropped: false,
            isDragging: false,
            isFlipping: false,
            savedTime: 0,
        };
    }

    export default {
        mixins: [playBoxGameMixin, olympiadAnswerTimerMixin],
        data() {
            return DEFAULT_DATA();
        },
        watch: {
            time(val) {
                this.counter = val;
                if (this.counter === 14) {
                    this.isFlipping = true;
                } else if (this.counter >= 16 && !this.gameStarted) {
                    this.isFlipping = false;
                    this.gameStarted = true;
                }
                if (this.counter >= 15 && !this.gameStarted) {
                    this.beforeStart();
                    this.dropMatrixInAnswerBox();
                }
            },
            isNeedRotate(val) {
                if (val) {
                    this.savedTime = this.time;
                } else {
                    !this.timer && this.setTimer();
                    this.time = this.savedTime;
                }
            },
        },
        methods: {
            fetchData() {
                this.getExercise()
                    .then(() => {
                        this.getExample();
                        this.registerSounds();
                        !this.isNeedRotate && !this.timer && this.setTimer();
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.$toasted.error(this.$root.$t('errors.getting_level'));
                        this.$router.push({ name: 'dashboard' });
                    });
            },
            beforeStart() {
                this.prepareForGame = false;
                this.$nextTick(() => this.startGame(), 10);
            },
            dropItemInMatrix(index) {
                this.itemsForAnswersBox.splice(index, 1);
                this.isDragging = false;
            },
            getExample() {
                this.isLoading = true;
                let paramsForRequest = {
                    type: this.parameters.viewType,
                    amountOfCells:
                        this.parameters.difficult.amountOfRows /* - 1 */ * this.parameters.difficult.amountOfColumns,
                    topics: this.parameters.selectedSubTopics,
                    locale: this.user.locale,
                };
                AssociationsGameApi.getGameItems(paramsForRequest)
                    .then(elements => {
                        this.classificationsTable = Object.values(elements).map(row =>
                            Object.values(row).map((link, linkIndex) => {
                                if (linkIndex === 0) {
                                    link.type = CORRECT_LINK;
                                }
                                return link;
                            })
                        );
                        this.correctMatrix = this.classificationsTable;
                        this.typeOfLinksInChain = 'image';
                        this.isLoading = false;
                    })
                    .catch(err => this.showErrorMessage(err));
            },
            refreshGameStatBeforeSend() {
                this.gameStat.chainOfAttempts = this.chainOfAttempts;
                this.gameStat.attempts = this.chainOfAttempts.length;
                this.gameStat.answers = this.goodAnswers;
                this.gameStat.percent = this.calculatePercent();
                this.gameStat.isCompleted = this.gameFinished;
                this.gameStat.executionTime = this.time;
                this.gameStat.isCorrect = true;
                this.gameStat.parameters = _.cloneDeep(this.parameters);
            },
            calculatePercent() {
                let total = this.goodAnswers + this.badAnswers;
                if (total > 0) {
                    return parseInt((this.goodAnswers / total) * 100);
                }
                return 0;
            },
            processReply() {
                let elemOfAttempt = {
                    good: 0,
                    bad: 0,
                };
                this.classificationsTable.forEach((row, rowIndex) => {
                    row.forEach((link, linkIndex) => {
                        if (link.type !== CORRECT_LINK) {
                            if (!link.helpMarker && this.isCorrectLink(rowIndex, linkIndex)) {
                                this.correctMatrix[rowIndex][linkIndex].type = CORRECT_LINK;
                                !this.isOlympiad &&
                                    (this.classificationsTable[rowIndex][linkIndex].type = CORRECT_LINK);
                                elemOfAttempt.good++;
                            } else {
                                elemOfAttempt.bad++;
                            }
                        }
                    });
                });
                this.chainOfAttempts.push(elemOfAttempt);
                this.helperModeOn = false;
                if (
                    _.flatten(this.classificationsTable).filter(link => link.type === CORRECT_LINK).length ===
                    _.flatten(this.correctMatrix).length
                ) {
                    this.isAttemptCorrect = true;
                    this.hideGameHeader = true;
                    if (this.amountOfExamples === 0) {
                        this.classificationsDone();
                        return;
                    }
                    if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                        this.sendStat();
                    }
                    this.resetData();
                    return;
                }
                if (!this.isOlympiad) {
                    this.dropMatrixInAnswerBox();
                }
                if (!this.$offline && (this.user.type === 'student' || this.user.type === 'olympiads-student')) {
                    this.isAttemptCorrect = false;
                    this.sendStat();
                }
            },
            swapOneTopicImagesInCorrectMatrix(rowIndex, firstImagePosition, secondImagePosition) {
                const temp = this.correctMatrix[rowIndex][firstImagePosition];
                this.correctMatrix[rowIndex][firstImagePosition] = this.correctMatrix[rowIndex][secondImagePosition];
                this.correctMatrix[rowIndex][secondImagePosition] = temp;
            },
            resetData() {
                this.hideGameHeader = false;
                this.gameStarted = false;
                this.memorized = false;
                this.stopTimer();
                this.timer = null;
                this.itemsForAnswersBox = [];
                this.classificationsTable = [];
                this.correctMatrix = [];
                if (this.answerTimer) {
                    clearInterval(this.answerTimer);
                    this.answerTimer = null;
                }
            },
            isCorrectLink(rowIndex, linkIndex) {
                const originalLink = this.correctMatrix[rowIndex][linkIndex];
                const userLink = this.classificationsTable[rowIndex][linkIndex];
                if (originalLink.id === userLink.id) {
                    return true;
                }
                if (originalLink.topic === userLink.topic) {
                    const secondImagePosition = this.correctMatrix[rowIndex].findIndex(link => link.id === userLink.id);
                    this.swapOneTopicImagesInCorrectMatrix(rowIndex, linkIndex, secondImagePosition);
                    return true;
                }
                return false;
            },
            classificationsDone() {
                if (!this.isOlympiad) {
                    this.soundInstance = this.$createjs.Sound.play('gameDone');
                    this.soundInstance.volume = DEFAULT_VOLUME;
                }
                this.gameFinished = true;
                this.sendStat();
            },
            startGame() {
                this.memorized = !this.memorized;
                if (this.answerDuration) {
                    this.startAnswerTimer();
                }
            },
            dropMatrixInAnswerBox() {
                this.linkDropped = true;
                this.itemsForAnswersBox = _.shuffle(
                    _.flatten(this.correctMatrix).filter(item => item.type !== CORRECT_LINK)
                );
                let typeForEmptyCell = 'dropZone';
                this.classificationsTable = this.classificationsTable.map(row =>
                    row.map((link, linkIndex) => {
                        if (linkIndex === 0) {
                            link.type = CORRECT_LINK;
                        } else if (link.type !== CORRECT_LINK) {
                            link = {
                                type: typeForEmptyCell,
                                value: '',
                            };
                        }
                        return link;
                    })
                );
                this.$nextTick(this.shakeAnswerBlocks);
            },
            shakeAnswerBlocks() {
                let answerBlocks = document.getElementsByClassName('answer');
                let amount = answerBlocks.length;
                let timeouts = [];
                for (let i = 0; i < amount; i++) {
                    timeouts.push(
                        setTimeout(() => {
                            answerBlocks[i]?.classList.add('shake');
                        }, i * 100)
                    );
                }
                setTimeout(() => {
                    for (let j = 0; j < timeouts.length; j++) {
                        clearTimeout(timeouts[j]);
                    }
                    for (let i = 0; i < amount; i++) {
                        if (answerBlocks[i]) {
                            answerBlocks[i].classList.remove('shake');
                        }
                    }
                }, 2000);
            },
            dropItemInAnswersBox(link) {
                this.linkDropped = true;
                if (link && link.from !== 'answers-box') {
                    let item = _.cloneDeep(link.item);
                    let dragEvent = arguments[arguments.length - 1];
                    let toDropIndex = dragEvent.srcElement.dataset.index;
                    if (toDropIndex) {
                        this.itemsForAnswersBox.splice(toDropIndex, 0, item);
                    } else {
                        if (dragEvent.srcElement.childElementCount) {
                            toDropIndex = dragEvent.srcElement.children[0].dataset.index;
                            toDropIndex && this.itemsForAnswersBox.splice(toDropIndex, 0, item);
                        }
                        !toDropIndex && this.itemsForAnswersBox.push(item);
                    }
                    this.$set(this.classificationsTable[link.rowIndex], link.index, { type: 'dropZone' });
                }
            },
            showCorrectAnswers() {
                for (let rowIndex = 0; rowIndex < this.classificationsTable.length; rowIndex++) {
                    for (let index = 0; index < this.classificationsTable[rowIndex].length; index++) {
                        if (!_.trim(this.classificationsTable[rowIndex][index].value)) {
                            this.$set(
                                this.classificationsTable[rowIndex],
                                index,
                                _.cloneDeep(this.correctMatrix[rowIndex][index])
                            );
                            this.classificationsTable[rowIndex][index].helpMarker = true;
                        } else {
                            this.classificationsTable[rowIndex][index].type = this.correctMatrix[rowIndex][index].type;
                            this.classificationsTable[rowIndex][index].helpMarker = false;
                        }
                    }
                }
                this.helperModeOn = true;
            },
            beforeGameRestart() {
                Object.assign(this, DEFAULT_DATA());
            },
        },
        computed: {
            isNeedRotate() {
                return this.isVerticalScreen && this.windowWidth < 992;
            },
            goodAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(attempt => (count += attempt.good));
                return count;
            },
            badAnswers() {
                let count = 0;
                this.chainOfAttempts.forEach(attempt => (count += attempt.bad));
                return count;
            },
            memorizedButtonName() {
                return this.$i18n.t('trainer.trainer_gamePlayBox_memorizedButton', {
                    mode: this.$i18n.t('trainer.trainer_gamePlayBox_memorizedButton_all'),
                });
            },
            allLinksNotSettled() {
                return this.itemsForAnswersBox.length !== 0;
            },
            disableReplyButton() {
                return this.allLinksNotSettled;
            },
            showAnswersContainer() {
                return this.gameStarted;
            },
        },
        components: {
            AnswerContainer,
            AssociationsTable,
            Drop,
        },
    };
</script>

<style scoped lang="scss">
    @import '@/assets/sass/games/playbox.scss';

    .play-box--warning {
        display: table;
    }

    .play-box__game {
        display: flex;
        flex: 1 1 auto;
        height: 100%;
        max-width: 85vw;
        flex-direction: column;
        justify-content: space-between;
    }

    .play-box__example {
        margin-bottom: 20px;
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: inherit;
        @include media($xlg) {
            justify-content: center;
            flex-direction: row;
        }
    }

    .play-box__field {
        padding: 0 20px;
        text-align: center;
        font-size: 18px;
        user-select: none;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .play-box__word {
        padding: 10px;
        margin: 0 10px 10px 0;
        background: $x-color-white;
        border-radius: $border-radius-5;

        word-break: break-all;

        cursor: pointer;
    }

    .play-box__word:last-child {
        margin-right: 0;
    }

    .play-box__word--selected {
        color: $x-color-white;
        background: $hsl-color-green-58;
    }

    .play-box__word--unselected {
        color: $x-color-white;
        background: $hsl-color-red-62;
    }

    .play-box__header {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        margin-bottom: 20px;
        @include media($sm) {
            margin-bottom: 30px;
        }
        @include media($md) {
            font-size: 32px;
            line-height: 38px;
        }
    }

    .play-box__title {
        margin-bottom: 20px;
    }

    .play-box__subtitle {
        margin-bottom: 0;
    }

    .play-box__end-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .play-box__reaction-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        width: 100%;
    }

    .play-box__time {
        font-weight: 500;
        font-size: 32px;
        line-height: 38px;
        text-align: center;
        color: $hsl-color-green-58;
        margin-bottom: 30px;
    }

    .play-box__answers {
        font-weight: 500;
        font-size: 18px;
        line-height: 21px;
        text-align: center;
        @include media($md) {
            font-size: 22px;
            line-height: 26px;
        }
        @include media($lg) {
            font-size: 32px;
            line-height: 38px;
        }
        @include media($xl) {
            font-size: 42px;
            line-height: 50px;
        }
    }

    .play-box__image {
        max-width: 100%;
        margin-bottom: 30px;
        @include media($md) {
            margin-bottom: 40px;
        }
    }

    .play-box__buttons {
        display: flex;
        flex-direction: column;
        margin-top: 20px;
        @include media($sm) {
            margin-top: 25px;
        }
        @include media($lg) {
            flex-direction: row;
        }
    }

    .play-box__buttons--game {
        margin: 30px 0;
        @include media($xlg) {
            margin: 0 0 0 30px;
        }
    }

    .play-box__button {
        margin: 20px 0;
        @include media($lg) {
            margin: 0 20px;
        }
    }

    .play-box__button:first-child {
        margin-top: 0;
        @include media($lg) {
            margin-left: 0;
        }
    }

    .play-box__button:last-child {
        margin-bottom: 0;
        @include media($lg) {
            margin-right: 0;
        }
    }

    .play-box__game-info {
        align-self: flex-end;
        @include media($md) {
            align-self: auto;
        }
    }

    .play-box__drop {
        display: flex;
        justify-content: center;
    }

    .warning {
        display: table-cell;
        vertical-align: middle;
        text-align: center;
    }
</style>
